<template>
  <div class="sentiment-explorer-tab p-4">
    <!-- Data Selection Controls -->
    <div class="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-4 sm:items-end min-w-0">
      <!-- Symbol Selector -->
      <div class="form-control w-full sm:max-w-xs">
        <label class="label">
          <span class="label-text">Symbol</span>
        </label>
        <div class="relative w-full">
          <input
            v-model="symbolInput"
            type="text"
            placeholder="Type or select symbol"
            class="input input-bordered w-full"
            @focus="showDropdown = true"
            @input="filterSymbols"
            @keydown.enter="selectSymbol(symbolInput)"
            @click.stop
          >
          <ul
            v-if="showDropdown && filteredSymbols.length > 0"
            class="absolute left-0 top-full mt-1 z-50 menu p-2 shadow-lg bg-base-100 rounded-box w-full max-h-60 overflow-y-auto border border-base-300"
            @click.stop
          >
            <li
              v-for="sym in filteredSymbols"
              :key="sym"
            >
              <a @click.stop="selectSymbol(sym)">{{ sym }}</a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Timeframe Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Timeframe</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="tf in timeframeOptions"
            :key="tf.value"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': timeframe === tf.value }"
            @click="timeframe = tf.value"
          >
            {{ tf.label }}
          </button>
        </div>
      </div>

      <!-- Days Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Days</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="d in availableDays"
            :key="d"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': days === d }"
            @click="days = d"
          >
            {{ d }}
          </button>
        </div>
      </div>

      <!-- End Date Selector -->
      <div class="form-control ml-4">
        <label class="label">
          <span class="label-text">End Date</span>
        </label>
        <div class="flex items-center gap-1">
          <button
            class="btn btn-xs sm:btn-sm"
            title="Go back 1 day"
            @click="adjustEndDate(-1)"
          >
            &lt;
          </button>
          <input
            v-model="endDate"
            type="date"
            class="input input-sm input-bordered w-36"
          >
          <button
            class="btn btn-xs sm:btn-sm"
            title="Go forward 1 day"
            @click="adjustEndDate(1)"
          >
            &gt;
          </button>
          <button
            class="btn btn-sm btn-ghost"
            title="Reset to today"
            @click="resetEndDate"
          >
            Today
          </button>
        </div>
      </div>

      <!-- Fetch Button -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">&nbsp;</span>
        </label>
        <button
          class="btn btn-primary w-full sm:w-auto"
          :disabled="isLoading || !symbol"
          @click="fetchSentimentData"
        >
          <span
            v-if="isLoading"
            class="loading loading-spinner loading-sm"
          />
          {{ isLoading ? 'Loading...' : 'Load Data' }}
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="alert alert-error mb-4"
    >
      <span>{{ error }}</span>
    </div>

    <!-- Master Signal Card (HAR-RV Enhanced) -->
    <div
      v-if="hasData"
      class="card shadow-xl mb-4 border"
      :class="masterSignalCardClass"
    >
      <!-- Status Bar -->
      <div
        class="h-2 rounded-t-xl"
        :class="masterSignalBarClass"
      />

      <div class="card-body p-4">
        <!-- Main Signal Row -->
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-2">
              <h2 class="text-2xl font-bold">
                {{ masterSignalLabel }}
              </h2>
              <span
                class="badge badge-lg"
                :class="confidenceBadgeClass"
              >
                {{ harData?.master_signal?.confidence || 'N/A' }}
              </span>
            </div>
            <p class="text-base opacity-80">
              {{ masterSignalExplanation }}
            </p>
          </div>

          <!-- Composite Score -->
          <div class="text-right">
            <div class="text-sm opacity-60 mb-1">
              Composite Score
            </div>
            <div class="text-3xl font-bold font-mono">
              {{ harData?.master_signal?.composite_score || 50 }}/100
            </div>
          </div>
        </div>

        <!-- Quick Stats Row -->
        <div class="flex flex-wrap gap-3 mt-4">
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Direction
            </div>
            <div
              class="stat-value text-lg"
              :class="directionTextClass"
            >
              {{ capitalizeFirst(harData?.direction_bias || 'neutral') }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Regime
            </div>
            <div
              class="stat-value text-lg"
              :class="regimeTextClass"
            >
              {{ formatRegime(harData?.regime) }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              IV Premium
            </div>
            <div
              class="stat-value text-lg font-mono"
              :class="spreadTextClass"
            >
              {{ harData?.spread ? (harData.spread > 0 ? '+' : '') + harData.spread.toFixed(1) + '%' : 'N/A' }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              IV/RV Ratio
            </div>
            <div
              class="stat-value text-lg font-mono"
              :class="ivRvColor"
            >
              {{ latestData.iv_rv_ratio?.toFixed(2) || 'N/A' }}
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="card-actions mt-4">
          <button
            class="btn btn-outline btn-sm"
            @click="showGrid = !showGrid"
          >
            {{ showGrid ? 'Hide' : 'View' }} Grid Parameters
          </button>
          <button
            class="btn btn-outline btn-sm"
            @click="showBreakdown = !showBreakdown"
          >
            {{ showBreakdown ? 'Hide' : 'Signal' }} Breakdown
          </button>
        </div>
      </div>
    </div>

    <!-- Grid Parameters Panel (Expandable) -->
    <div
      v-if="hasData && showGrid && harData?.grid"
      class="card bg-base-200 mb-4"
    >
      <div class="card-body p-4">
        <h3 class="font-bold text-lg mb-4">
          Recommended Grid Parameters
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Left: Parameters -->
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="opacity-70">Direction</span>
              <span class="font-bold">
                {{ capitalizeFirst(harData?.direction_bias || 'neutral') }}
                ({{ harData?.grid?.buy_levels?.length || 0 }} buy / {{ harData?.grid?.sell_levels?.length || 0 }} sell)
              </span>
            </div>
            <div class="flex justify-between">
              <span class="opacity-70">Current Price</span>
              <span class="font-mono">${{ formatPrice(latestData.price) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="opacity-70">Grid Spacing</span>
              <span>{{ harData?.grid?.spacing_pct?.toFixed(2) || '1.00' }}%</span>
            </div>
            <div class="flex justify-between">
              <span class="opacity-70">Position Size Mult</span>
              <span>{{ harData?.grid?.position_size_mult?.toFixed(2) || '0.50' }}x</span>
            </div>
          </div>

          <!-- Right: Ranges -->
          <div class="space-y-3">
            <div>
              <div class="text-success font-semibold">
                Buy Range
              </div>
              <div class="font-mono">
                ${{ formatPrice(harData?.grid?.buy_range?.[0]) }} &rarr; ${{ formatPrice(harData?.grid?.buy_range?.[1]) }}
              </div>
            </div>
            <div>
              <div class="text-error font-semibold">
                Sell Range
              </div>
              <div class="font-mono">
                ${{ formatPrice(harData?.grid?.sell_range?.[0]) }} &rarr; ${{ formatPrice(harData?.grid?.sell_range?.[1]) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Copy Button -->
        <div class="mt-4">
          <button
            class="btn btn-primary btn-sm"
            @click="copyGridToClipboard"
          >
            Copy Grid Parameters
          </button>
        </div>
      </div>
    </div>

    <!-- Signal Breakdown Panel (Expandable) -->
    <div
      v-if="hasData && showBreakdown && harData?.master_signal"
      class="card bg-base-200 mb-4"
    >
      <div class="card-body p-4">
        <h3 class="font-bold text-lg mb-4">
          Signal Breakdown - How We Arrived at This Signal
        </h3>

        <!-- 1. HAR REGIME EXPLANATION -->
        <div class="mb-6 p-4 bg-base-300 rounded-lg">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-lg font-bold">1. HAR Regime</span>
            <span
              class="badge"
              :class="regimeTextClass"
            >{{ formatRegime(harData?.regime) }}</span>
            <span class="text-sm opacity-60">(Weight: 40%)</span>
          </div>

          <!-- Formula and Calculation -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <div class="text-xs opacity-60 mb-1">
                Formula
              </div>
              <div class="font-mono text-sm bg-base-100 p-2 rounded">
                Spread = IV (DVOL) - RV Forecast
              </div>
            </div>
            <div>
              <div class="text-xs opacity-60 mb-1">
                Calculation
              </div>
              <div class="font-mono text-sm bg-base-100 p-2 rounded">
                {{ harData?.regime_explanation?.calculation || 'N/A' }}
              </div>
            </div>
          </div>

          <!-- Values -->
          <div class="grid grid-cols-3 gap-2 mb-3">
            <div class="text-center p-2 bg-base-100 rounded">
              <div class="text-xs opacity-60">
                IV (DVOL)
              </div>
              <div class="font-mono font-bold">
                {{ harData?.current_iv?.toFixed(1) || 'N/A' }}%
              </div>
            </div>
            <div class="text-center p-2 bg-base-100 rounded">
              <div class="text-xs opacity-60">
                RV Forecast
              </div>
              <div class="font-mono font-bold">
                {{ harData?.rv_forecast?.toFixed(1) || 'N/A' }}%
              </div>
            </div>
            <div
              class="text-center p-2 bg-base-100 rounded"
              :class="spreadTextClass"
            >
              <div class="text-xs opacity-60">
                Spread
              </div>
              <div class="font-mono font-bold">
                {{ harData?.spread ? (harData.spread > 0 ? '+' : '') + harData.spread.toFixed(1) + '%' : 'N/A' }}
              </div>
            </div>
          </div>

          <!-- Thresholds Visual -->
          <div class="mb-3">
            <div class="text-xs opacity-60 mb-1">
              Regime Thresholds
            </div>
            <div class="flex items-center gap-1 text-xs">
              <span class="px-2 py-1 bg-error/20 rounded">DANGER &lt;{{ harData?.regime_thresholds?.danger }}%</span>
              <span class="px-2 py-1 bg-orange-500/20 rounded">CAUTION</span>
              <span class="px-2 py-1 bg-warning/20 rounded">NEUTRAL</span>
              <span class="px-2 py-1 bg-success/20 rounded">FAVORABLE &gt;{{ harData?.regime_thresholds?.favorable }}%</span>
              <span class="px-2 py-1 bg-success/40 rounded">HARVEST &gt;{{ harData?.regime_thresholds?.premium_harvest }}%</span>
            </div>
          </div>

          <!-- Interpretation -->
          <div class="text-sm opacity-80 italic">
            {{ harData?.regime_explanation?.interpretation || '' }}
          </div>
        </div>

        <!-- 2. IV/RV RATIO -->
        <div class="mb-6 p-4 bg-base-300 rounded-lg">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-lg font-bold">2. IV/RV Ratio</span>
            <span
              class="badge"
              :class="ivRvColor"
            >{{ latestData.iv_rv_ratio?.toFixed(2) || 'N/A' }}</span>
            <span class="text-sm opacity-60">(Weight: 30%)</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <div class="text-xs opacity-60 mb-1">
                Formula
              </div>
              <div class="font-mono text-sm bg-base-100 p-2 rounded">
                IV/RV = DVOL / Realized Vol (14d)
              </div>
            </div>
            <div>
              <div class="text-xs opacity-60 mb-1">
                Calculation
              </div>
              <div class="font-mono text-sm bg-base-100 p-2 rounded">
                {{ latestData.dvol?.toFixed(1) || '?' }}% / {{ latestData.realized_vol?.toFixed(1) || '?' }}% = {{ latestData.iv_rv_ratio?.toFixed(2) || '?' }}
              </div>
            </div>
          </div>

          <div class="text-sm opacity-80 italic">
            {{ ivRvInterpretation }}. Ratio &gt;1.4 = fear (BUY opportunity), &lt;1.0 = complacency (CAUTION).
          </div>
        </div>

        <!-- 3. DIRECTION BIAS -->
        <div class="mb-6 p-4 bg-base-300 rounded-lg">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-lg font-bold">3. Direction Bias</span>
            <span
              class="badge"
              :class="directionTextClass"
            >{{ capitalizeFirst(harData?.direction_bias || 'neutral') }}</span>
            <span class="text-sm opacity-60">(Weight: 15%)</span>
          </div>

          <!-- Direction Components -->
          <div
            v-if="harData?.direction_explanation?.components"
            class="space-y-2 mb-3"
          >
            <div
              v-for="comp in harData.direction_explanation.components"
              :key="comp.factor"
              class="flex items-start gap-3 p-2 bg-base-100 rounded"
            >
              <div class="w-32 font-medium text-sm">
                {{ comp.factor }}
              </div>
              <div
                class="w-16 font-mono text-sm"
                :class="comp.score > 0 ? 'text-success' : comp.score < 0 ? 'text-error' : 'text-warning'"
              >
                {{ comp.score > 0 ? '+' : '' }}{{ comp.score?.toFixed(2) || '0' }}
              </div>
              <div class="flex-1 text-sm opacity-80">
                {{ comp.explanation }}
              </div>
            </div>
          </div>

          <div class="text-sm opacity-80 italic">
            {{ harData?.direction_reason || 'Direction calculated from options skew, price momentum, and RV term structure.' }}
          </div>
        </div>

        <!-- 4. FUNDING RATE -->
        <div class="mb-6 p-4 bg-base-300 rounded-lg">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-lg font-bold">4. Funding Rate</span>
            <span
              class="badge"
              :class="latestData.funding_rate > 0.02 ? 'badge-error' : latestData.funding_rate < -0.01 ? 'badge-success' : 'badge-warning'"
            >{{ latestData.funding_rate?.toFixed(4) || 'N/A' }}%</span>
            <span class="text-sm opacity-60">(Weight: 15%)</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <div class="text-xs opacity-60 mb-1">
                Current Funding
              </div>
              <div class="font-mono text-sm bg-base-100 p-2 rounded">
                {{ latestData.funding_rate?.toFixed(4) || 'N/A' }}% per 8h
              </div>
            </div>
            <div>
              <div class="text-xs opacity-60 mb-1">
                Z-Score (vs 30d avg)
              </div>
              <div
                class="font-mono text-sm bg-base-100 p-2 rounded"
                :class="harData?.funding_zscore > 1 ? 'text-error' : harData?.funding_zscore < -1 ? 'text-success' : ''"
              >
                {{ harData?.funding_zscore?.toFixed(2) || 'N/A' }} σ
              </div>
            </div>
          </div>

          <div class="text-sm opacity-80 italic">
            {{ getFundingInterpretation() }}
          </div>
        </div>

        <!-- COMPOSITE SCORE -->
        <div class="p-4 bg-base-300 rounded-lg">
          <div class="flex items-center justify-between mb-2">
            <span class="text-lg font-bold">Composite Score</span>
            <span class="font-mono text-xl">{{ harData?.master_signal?.composite_score || 50 }}/100</span>
          </div>
          <progress
            class="progress w-full h-3"
            :class="compositeProgressClass"
            :value="harData?.master_signal?.composite_score || 50"
            max="100"
          />
          <div class="text-xs opacity-60 mt-2">
            Score = (Regime × 40%) + (IV/RV × 30%) + (Direction × 15%) + (Funding × 15%)
          </div>
          <div class="text-sm mt-2">
            <strong>Raw Score:</strong> {{ harData?.master_signal?.raw_score?.toFixed(3) || '0' }}
            → Thresholds: &gt;0.5 = Favorable Long, &gt;0.25 = Mild Favorable, ±0.25 = Neutral, &lt;-0.25 = Caution, &lt;-0.5 = Unfavorable
          </div>
        </div>
      </div>
    </div>

    <!-- Market Maker Stats Panel -->
    <div
      v-if="hasData"
      class="mb-4 stats shadow w-full bg-base-200 stats-vertical lg:stats-horizontal"
    >
      <div class="stat">
        <div class="stat-title">
          Current Price
        </div>
        <div class="stat-value text-lg text-primary">
          ${{ formatPrice(latestData.price) }}
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Implied Vol (DVOL)
        </div>
        <div
          class="stat-value text-lg"
          :class="ivColor"
        >
          {{ latestData.dvol ? latestData.dvol.toFixed(1) + '%' : 'N/A' }}
        </div>
        <div class="stat-desc">
          IV Percentile: {{ latestData.iv_percentile?.toFixed(0) || 'N/A' }}%
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Realized Vol (14d)
        </div>
        <div class="stat-value text-lg">
          {{ latestData.realized_vol ? latestData.realized_vol.toFixed(1) + '%' : 'N/A' }}
        </div>
        <div class="stat-desc">
          Historical price volatility
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          IV/RV Ratio
        </div>
        <div
          class="stat-value text-lg"
          :class="ivRvColor"
        >
          {{ latestData.iv_rv_ratio?.toFixed(2) || 'N/A' }}
        </div>
        <div class="stat-desc">
          {{ ivRvInterpretation }}
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Vol Premium
        </div>
        <div
          class="stat-value text-lg"
          :class="volPremiumColor"
        >
          {{ formatVolPremium(latestData.vol_premium) }}
        </div>
        <div class="stat-desc">
          IV - RV spread
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          MM Signal
        </div>
        <div
          class="stat-value text-lg"
          :class="mmSignalColor"
        >
          {{ latestData.mm_signal || 'N/A' }}
        </div>
        <div class="stat-desc">
          {{ mmSignalDesc }}
        </div>
      </div>
    </div>

    <!-- Supporting Analysis - Always Visible -->
    <div
      v-if="hasData"
      class="space-y-4"
    >
      <!-- HAR-RV Metrics Card -->
      <div
        v-if="harData"
        class="card bg-base-200"
      >
        <div class="card-body p-4">
          <h4 class="font-semibold text-lg mb-3">
            HAR-RV Analysis
          </h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="p-3 bg-base-300 rounded-lg">
              <div class="text-xs opacity-60">
                RV Forecast
              </div>
              <div class="text-lg font-mono font-bold">
                {{ harData.rv_forecast?.toFixed(1) || 'N/A' }}%
              </div>
            </div>
            <div class="p-3 bg-base-300 rounded-lg">
              <div class="text-xs opacity-60">
                Confidence
              </div>
              <div class="text-lg font-mono font-bold">
                {{ ((harData.confidence || 0) * 100).toFixed(0) }}%
              </div>
            </div>
            <div class="p-3 bg-base-300 rounded-lg">
              <div class="text-xs opacity-60">
                Options Skew
              </div>
              <div
                class="text-lg font-mono font-bold"
                :class="harData?.skew > 0 ? 'text-error' : harData?.skew < 0 ? 'text-success' : ''"
              >
                {{ harData?.skew !== null ? (harData.skew > 0 ? '+' : '') + harData.skew.toFixed(1) + '%' : 'N/A' }}
              </div>
              <div class="text-xs opacity-50">
                {{ harData?.skew > 0 ? 'Bearish' : harData?.skew < 0 ? 'Bullish' : '' }}
              </div>
            </div>
            <div class="p-3 bg-base-300 rounded-lg">
              <div class="text-xs opacity-60">
                RV Components
              </div>
              <div class="text-sm font-mono">
                D: {{ harData.rv_components?.daily?.toFixed(0) || '-' }} |
                W: {{ harData.rv_components?.weekly?.toFixed(0) || '-' }} |
                M: {{ harData.rv_components?.monthly?.toFixed(0) || '-' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel 0: Price with Vol Regime Overlay -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold text-lg">
            Price with Volatility Regime Overlay
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-success badge-sm">Green = High IV/RV (BUY opportunity)</span>
            <span class="badge badge-warning badge-sm">Yellow = Low IV/RV (CAUTION)</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Green = IV/RV &gt; 1.4 (fear = buying opportunity). Yellow = IV/RV &lt; 1.0 (complacency = caution). Based on mean-reversion approach.
        </p>
        <div
          ref="overlayChart"
          class="chart-panel-large"
        />
      </div>

      <!-- Panel 1: IV vs RV Comparison -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            Implied Vol (DVOL) vs Realized Vol
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-warning badge-sm">Orange = IV (DVOL)</span>
            <span class="badge badge-info badge-sm">Blue = RV (14d)</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          When IV is above RV, options are expensive (vol premium). When IV is below RV, options are cheap (vol discount).
        </p>
        <div
          ref="ivRvChart"
          class="chart-panel"
        />
      </div>

      <!-- Panel 2: IV/RV Ratio -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            IV/RV Ratio (Volatility Premium)
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-success badge-sm">&gt;1.4 = Fear (BUY opportunity)</span>
            <span class="badge badge-warning badge-sm">&lt;1.0 = Complacency (CAUTION)</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Mean-reversion approach: High IV/RV (&gt;1.4) = fear = buying opportunity. Low IV/RV (&lt;1.0) = complacency = caution.
        </p>
        <div
          ref="ivRvRatioChart"
          class="chart-panel"
        />
      </div>

      <!-- Panel 3: IV Percentile Rank -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            IV Percentile Rank (90-day)
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-success badge-sm">&gt;80% = Fear Extreme (BUY opportunity)</span>
            <span class="badge badge-warning badge-sm">&lt;20% = Complacency (CAUTION)</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Mean-reversion: High IV percentile = fear extreme = buying opportunity. Low = complacency = expect volatility expansion.
        </p>
        <div
          ref="ivPercentileChart"
          class="chart-panel"
        />
      </div>

      <!-- Panel 4: Funding Rate -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            Perpetual Funding Rate
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-error badge-sm">High Positive = Longs Pay</span>
            <span class="badge badge-success badge-sm">Negative = Shorts Pay</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Confirms directional bias. High funding = overleveraged longs. Negative funding = overleveraged shorts.
        </p>
        <div
          ref="fundingChart"
          class="chart-panel"
        />
      </div>

      <!-- Panel 5: Regime Score -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            Market Maker Regime Score
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-success badge-sm">Positive = BUY (Fear elevated)</span>
            <span class="badge badge-warning badge-sm">Negative = CAUTION (Complacency)</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Combined signal from IV/RV ratio (50%), IV percentile (30%), and funding (20%). Positive = fear = BUY opportunity. Negative = complacency = CAUTION.
        </p>
        <div
          ref="regimeChart"
          class="chart-panel"
        />
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!hasData && !isLoading"
      class="bg-base-200 rounded-lg p-8 text-center"
    >
      <p class="text-lg text-base-content/50 mb-4">
        Select a symbol and click "Load Data" to view the Market Maker dashboard
      </p>
      <div class="text-sm text-base-content/40 max-w-2xl mx-auto">
        <p class="mb-2">
          <strong>Market Maker Mindset - Using Implied Volatility:</strong>
        </p>
        <ul class="text-left list-disc list-inside space-y-1">
          <li><strong>IV (DVOL):</strong> Implied volatility from Deribit options</li>
          <li><strong>RV (Realized Vol):</strong> Historical price volatility (14-day)</li>
          <li><strong>IV/RV Ratio:</strong> Is options premium expensive or cheap?</li>
          <li><strong>IV Percentile:</strong> Where IV ranks vs historical range</li>
          <li><strong>Vol Premium:</strong> The spread between IV and RV</li>
        </ul>
      </div>
    </div>

    <!-- How to Use Guide -->
    <div
      v-if="hasData"
      class="mt-6 collapse collapse-arrow bg-base-200 rounded-lg"
    >
      <input type="checkbox">
      <div class="collapse-title font-semibold">
        Mean-Reversion Framework - How to Use for Grid Trading
      </div>
      <div class="collapse-content text-sm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div class="p-3 bg-success/10 rounded-lg">
            <h5 class="font-bold text-success mb-2">
              BUY Signal (IV/RV &gt; 1.4)
            </h5>
            <ul class="list-disc list-inside space-y-1 text-base-content/80">
              <li>IV/RV Ratio &gt; 1.4 = Fear elevated</li>
              <li>IV Percentile &gt; 80% = Extreme fear</li>
              <li>High IV often marks BOTTOMS in crypto</li>
              <li><strong>Action:</strong> Deploy LONG directional grids</li>
              <li><strong>Grid spacing:</strong> 3-5% (wider = more aggressive)</li>
              <li><strong>Hold period:</strong> 14-30 days for best results</li>
            </ul>
          </div>
          <div class="p-3 bg-warning/10 rounded-lg">
            <h5 class="font-bold text-warning mb-2">
              CAUTION Signal (IV/RV &lt; 1.0)
            </h5>
            <ul class="list-disc list-inside space-y-1 text-base-content/80">
              <li>IV/RV Ratio &lt; 1.0 = Complacency (rare in crypto)</li>
              <li>IV Percentile &lt; 20% = Extreme complacency</li>
              <li>Volatility expansion likely coming</li>
              <li><strong>Action:</strong> Reduce grid exposure</li>
              <li><strong>Grid spacing:</strong> 1-2% (tighter = defensive)</li>
              <li><strong>Capital:</strong> Reduce to 10-25%</li>
            </ul>
          </div>
        </div>
        <div class="mt-4 p-3 bg-info/10 rounded-lg">
          <h5 class="font-bold text-info mb-2">
            Why Mean-Reversion Works (Backtested 2023-2024)
          </h5>
          <ul class="list-disc list-inside space-y-1 text-base-content/80">
            <li><strong>Fear peaks mark bottoms:</strong> High IV/RV &gt; 1.4 historically preceded rallies</li>
            <li><strong>Positive spot/vol correlation:</strong> In bull markets, IV rises WITH price</li>
            <li><strong>Complacency precedes volatility:</strong> IV/RV &lt; 1.0 often precedes big moves</li>
            <li><strong>"Buy blood in streets":</strong> Fear = opportunity, not danger</li>
            <li><strong>Combine with funding:</strong> High IV/RV + negative funding = strongest BUY signal</li>
          </ul>
        </div>
        <div class="mt-4 p-3 bg-base-300 rounded-lg">
          <h5 class="font-bold mb-2">
            Grid Trading Rules Summary
          </h5>
          <table class="table table-xs">
            <thead>
              <tr>
                <th>IV/RV Level</th>
                <th>Signal</th>
                <th>Grid Spacing</th>
                <th>Capital %</th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-success">
                <td>&gt; 1.6</td>
                <td>Strong BUY</td>
                <td>4-5%</td>
                <td>100%</td>
              </tr>
              <tr class="text-success">
                <td>1.4 - 1.6</td>
                <td>BUY</td>
                <td>3-4%</td>
                <td>75%</td>
              </tr>
              <tr>
                <td>1.0 - 1.4</td>
                <td>Neutral</td>
                <td>2-3%</td>
                <td>50%</td>
              </tr>
              <tr class="text-warning">
                <td>&lt; 1.0</td>
                <td>CAUTION</td>
                <td>1-2%</td>
                <td>25%</td>
              </tr>
              <tr class="text-warning">
                <td>&lt; 0.85</td>
                <td>Strong CAUTION</td>
                <td>1%</td>
                <td>10%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import Plotly from 'plotly.js-dist-min';
import api from '@/api/axios';

// Common symbols
const commonSymbols = [
  'BTCUSDT',
  'ETHUSDT',
  'SOLUSDT',
  'XRPUSDT',
  'BNBUSDT',
  'ADAUSDT',
  'DOGEUSDT',
  'AVAXUSDT',
  'DOTUSDT',
  'MATICUSDT',
  'LINKUSDT',
  'UNIUSDT',
  'ATOMUSDT',
  'LTCUSDT',
];

const timeframeOptions = [
  { value: '1h', label: '1 Hour' },
  { value: '4h', label: '4 Hour' },
  { value: '1d', label: 'Daily' },
];

const dayOptionsByTimeframe = {
  '1h': [30, 60, 90, 180],
  '4h': [30, 60, 90, 180],
  '1d': [30, 60, 90, 180],
};

// State
const overlayChart = ref(null);
const ivRvChart = ref(null);
const ivRvRatioChart = ref(null);
const ivPercentileChart = ref(null);
const fundingChart = ref(null);
const regimeChart = ref(null);

const symbolInput = ref('BTCUSDT');
const symbol = ref('BTCUSDT');
const showDropdown = ref(false);
const filteredSymbols = ref([...commonSymbols]);
const timeframe = ref('1h');
const days = ref(180);
const endDate = ref(new Date().toISOString().split('T')[0]); // Today's date as default
const sentimentData = ref([]);
const latestData = ref({});
const harData = ref(null);
const isLoading = ref(false);
const error = ref(null);

// UI state for expandable panels
const showGrid = ref(false);
const showBreakdown = ref(false);

// Helper functions for end date
const adjustEndDate = (deltaDays) => {
  const current = new Date(endDate.value);
  current.setDate(current.getDate() + deltaDays);
  const today = new Date();
  // Don't allow future dates
  if (current <= today) {
    endDate.value = current.toISOString().split('T')[0];
    if (hasData.value) {
      fetchSentimentData();
    }
  }
};

const resetEndDate = () => {
  endDate.value = new Date().toISOString().split('T')[0];
  if (hasData.value) {
    fetchSentimentData();
  }
};

// Computed
const hasData = computed(() => sentimentData.value.length > 0);

const availableDays = computed(() => {
  return dayOptionsByTimeframe[timeframe.value] || [30, 60, 90];
});

const latestRegimeScore = computed(() => {
  if (!hasData.value) return 0;
  const data = sentimentData.value;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].regime_score !== null && data[i].regime_score !== undefined) {
      return data[i].regime_score;
    }
  }
  return 0;
});

const mmSignal = computed(() => {
  const score = latestRegimeScore.value;
  const ivRv = latestData.value.iv_rv_ratio;

  // Mean-reversion approach:
  // Positive score = fear elevated = BUY opportunity
  // Negative score = complacency = CAUTION
  if (score > 0.6) {
    return {
      title: 'Fear Extreme - Strong BUY Opportunity',
      description: `IV/RV at ${ivRv?.toFixed(2) || 'N/A'} (elevated). Market in fear - historically marks bottoms.`,
      action: 'BUY'
    };
  } else if (score > 0.3) {
    return {
      title: 'Fear Elevated - BUY Opportunity',
      description: 'Vol premium elevated. Good accumulation zone.',
      action: 'LEAN BUY'
    };
  } else if (score > -0.3) {
    return {
      title: 'Neutral Zone',
      description: 'IV approximately equals RV. No strong signal from vol premium.',
      action: 'NEUTRAL'
    };
  } else if (score > -0.6) {
    return {
      title: 'Complacency Building - Mild Caution',
      description: 'Vol premium falling. Be cautious, volatility expansion possible.',
      action: 'CAUTION'
    };
  } else {
    return {
      title: 'Complacency Extreme - Strong Caution',
      description: `IV/RV at ${ivRv?.toFixed(2) || 'N/A'} (low). Market complacent - big move likely coming.`,
      action: 'CAUTION'
    };
  }
});

const signalCardClass = computed(() => {
  const score = latestRegimeScore.value;
  // Mean-reversion: positive = BUY (green), negative = CAUTION (yellow/orange)
  if (score > 0.6) return 'bg-success/20 border-2 border-success';
  if (score > 0.3) return 'bg-success/10 border border-success/50';
  if (score > -0.3) return 'bg-base-300 border border-base-content/20';
  if (score > -0.6) return 'bg-warning/10 border border-warning/50';
  return 'bg-warning/20 border-2 border-warning';
});

const ivColor = computed(() => {
  const ivPct = latestData.value.iv_percentile;
  if (ivPct === null || ivPct === undefined) return '';
  // Mean-reversion: High IV = fear = BUY opportunity (green)
  if (ivPct > 80) return 'text-success';  // Fear extreme = BUY
  if (ivPct < 20) return 'text-warning';  // Complacency = CAUTION
  return 'text-info';
});

const ivRvColor = computed(() => {
  const ratio = latestData.value.iv_rv_ratio;
  if (ratio === null || ratio === undefined) return '';
  // Mean-reversion: High IV/RV = fear = BUY, Low IV/RV = complacency = CAUTION
  if (ratio > 1.4) return 'text-success';  // Fear = BUY opportunity
  if (ratio < 1.0) return 'text-warning';  // Complacency = CAUTION
  return 'text-info';
});

const ivRvInterpretation = computed(() => {
  const ratio = latestData.value.iv_rv_ratio;
  if (ratio === null || ratio === undefined) return '';
  // Mean-reversion interpretation
  if (ratio > 1.4) return 'Fear (BUY opportunity)';
  if (ratio < 1.0) return 'Complacency (CAUTION)';
  return 'Neutral';
});

const volPremiumColor = computed(() => {
  const premium = latestData.value.vol_premium;
  if (premium === null || premium === undefined) return '';
  if (premium > 10) return 'text-success';
  if (premium < -10) return 'text-error';
  return 'text-warning';
});

const mmSignalColor = computed(() => {
  const signal = latestData.value.mm_signal;
  if (!signal) return '';
  // Mean-reversion: BUY = green, CAUTION = warning/yellow
  if (signal.includes('BUY')) return 'text-success';
  if (signal.includes('CAUTION')) return 'text-warning';
  return 'text-info';
});

const mmSignalDesc = computed(() => {
  const signal = latestData.value.mm_signal;
  if (!signal) return '';
  // Mean-reversion descriptions
  if (signal.includes('BUY')) return 'Fear elevated - BUY zone';
  if (signal.includes('CAUTION')) return 'Complacency - be careful';
  return 'Neutral zone';
});

// HAR-RV Master Signal computed properties
const masterSignalLabel = computed(() => {
  return harData.value?.master_signal?.label || mmSignal.value.action || 'NEUTRAL';
});

const masterSignalExplanation = computed(() => {
  return harData.value?.master_signal?.explanation || mmSignal.value.description || 'Loading analysis...';
});

const masterSignalCardClass = computed(() => {
  const signal = harData.value?.master_signal?.signal;
  const label = harData.value?.master_signal?.label || '';
  if (!signal) return signalCardClass.value;

  // Handle "FAVORABLE - NO DIRECTION" case
  if (label.includes('NO DIRECTION')) {
    return 'bg-info/10 border-info/50';
  }

  switch (signal) {
    case 'FAVORABLE_LONG':
      return 'bg-success/10 border-success/50';
    case 'FAVORABLE_SHORT':
      return 'bg-error/10 border-error/50';
    case 'CAUTION':
      return 'bg-orange-500/10 border-orange-500/50';
    case 'UNFAVORABLE':
      return 'bg-error/20 border-error';
    default:
      return 'bg-warning/10 border-warning/50';
  }
});

const masterSignalBarClass = computed(() => {
  const signal = harData.value?.master_signal?.signal;
  const label = harData.value?.master_signal?.label || '';
  if (!signal) return 'bg-warning';

  // Handle "FAVORABLE - NO DIRECTION" case
  if (label.includes('NO DIRECTION')) {
    return 'bg-info';
  }

  switch (signal) {
    case 'FAVORABLE_LONG':
      return 'bg-success';
    case 'FAVORABLE_SHORT':
      return 'bg-error';
    case 'CAUTION':
      return 'bg-orange-500';
    case 'UNFAVORABLE':
      return 'bg-error';
    default:
      return 'bg-warning';
  }
});

const confidenceBadgeClass = computed(() => {
  const confidence = harData.value?.master_signal?.confidence;
  if (confidence === 'HIGH') return 'badge-success';
  if (confidence === 'MEDIUM') return 'badge-warning';
  return 'badge-ghost';
});

const directionTextClass = computed(() => {
  const direction = harData.value?.direction_bias;
  if (direction === 'bullish') return 'text-success';
  if (direction === 'bearish') return 'text-error';
  return 'text-warning';
});

const regimeTextClass = computed(() => {
  const regime = harData.value?.regime;
  if (regime === 'PREMIUM_HARVEST') return 'text-success';
  if (regime === 'FAVORABLE') return 'text-success';
  if (regime === 'CAUTION') return 'text-orange-500';
  if (regime === 'DANGER') return 'text-error';
  return 'text-warning';
});

const spreadTextClass = computed(() => {
  const spread = harData.value?.spread;
  if (spread === null || spread === undefined) return '';
  if (spread > 5) return 'text-success';
  if (spread < -5) return 'text-error';
  return 'text-warning';
});

const compositeProgressClass = computed(() => {
  const score = harData.value?.master_signal?.composite_score || 50;
  if (score >= 65) return 'progress-success';
  if (score <= 35) return 'progress-error';
  return 'progress-warning';
});

// Helper functions
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatRegime = (regime) => {
  if (!regime) return 'N/A';
  return regime.replace('_', ' ').split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
};

const getFundingInterpretation = () => {
  const funding = latestData.value?.funding_rate;
  const zscore = harData.value?.funding_zscore;

  if (funding === null || funding === undefined) {
    return 'Funding rate data unavailable.';
  }

  let interpretation = '';

  // Interpret the raw funding rate
  if (funding > 0.05) {
    interpretation = 'Very high positive funding - longs paying shorts heavily. Market extremely leveraged long.';
  } else if (funding > 0.02) {
    interpretation = 'High positive funding - longs paying shorts. Bullish sentiment but potential for squeeze.';
  } else if (funding > 0.005) {
    interpretation = 'Moderate positive funding - slight bullish bias in perpetuals market.';
  } else if (funding < -0.02) {
    interpretation = 'Negative funding - shorts paying longs. Bearish sentiment but potential for short squeeze.';
  } else if (funding < -0.005) {
    interpretation = 'Slightly negative funding - mild bearish bias in perpetuals market.';
  } else {
    interpretation = 'Neutral funding - balanced market positioning.';
  }

  // Add z-score context
  if (zscore !== null && zscore !== undefined) {
    if (zscore > 2) {
      interpretation += ' Z-score very high - funding unusually elevated vs 30-day average.';
    } else if (zscore > 1) {
      interpretation += ' Z-score elevated - funding above average.';
    } else if (zscore < -1.5) {
      interpretation += ' Z-score very low - funding unusually depressed vs average.';
    } else if (zscore < -0.5) {
      interpretation += ' Z-score low - funding below average.';
    }
  }

  return interpretation;
};

const copyGridToClipboard = () => {
  if (!harData.value?.grid) return;

  const grid = harData.value.grid;
  const text = `Grid Parameters
Direction: ${capitalizeFirst(harData.value?.direction_bias || 'neutral')}
Spacing: ${grid.spacing_pct?.toFixed(2)}%
Position Size: ${grid.position_size_mult?.toFixed(2)}x

Buy Levels (${grid.buy_levels?.length}):
${grid.buy_levels?.map(l => '$' + formatPrice(l)).join('\n')}

Sell Levels (${grid.sell_levels?.length}):
${grid.sell_levels?.map(l => '$' + formatPrice(l)).join('\n')}`;

  navigator.clipboard.writeText(text).then(() => {
    // Could add a toast notification here
    console.log('Grid copied to clipboard');
  });
};

// Format helpers
const formatPrice = (price) => {
  if (typeof price !== 'number' || isNaN(price)) return '0.00';
  if (price >= 1000) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(price);
};

const formatVolPremium = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return (value >= 0 ? '+' : '') + value.toFixed(1) + '%';
};

// Symbol filtering
const filterSymbols = () => {
  const query = symbolInput.value.toUpperCase();
  filteredSymbols.value = commonSymbols.filter(s => s.includes(query));
  showDropdown.value = true;
};

const selectSymbol = (sym) => {
  symbolInput.value = sym;
  symbol.value = sym;
  showDropdown.value = false;
};

// Fetch sentiment data
const fetchSentimentData = async () => {
  if (symbolInput.value && symbolInput.value !== symbol.value) {
    symbol.value = symbolInput.value.toUpperCase().replace('/', '').trim();
  }

  if (!symbol.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.get('/api/market/sentiment/', {
      params: {
        symbol: symbol.value,
        days: days.value,
        exchange: 'bybit',
        timeframe: timeframe.value,
        end_date: endDate.value,
      },
    });

    if (response.data && response.data.data) {
      sentimentData.value = response.data.data;
      latestData.value = response.data.latest || {};
      harData.value = response.data.har || null;
      await nextTick();
      renderAllCharts();
    } else {
      error.value = 'No data returned from API';
    }
  } catch (err) {
    console.error('Error fetching sentiment data:', err);
    error.value = err.response?.data?.error || err.message || 'Failed to fetch data';
  } finally {
    isLoading.value = false;
  }
};

// Common chart config
const commonLayout = {
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
  font: { color: '#9ca3af', size: 11 },
  margin: { t: 10, b: 40, l: 60, r: 20 },
  showlegend: false,
  xaxis: {
    gridcolor: 'rgba(156, 163, 175, 0.1)',
    linecolor: 'rgba(156, 163, 175, 0.2)',
  },
  yaxis: {
    gridcolor: 'rgba(156, 163, 175, 0.1)',
    linecolor: 'rgba(156, 163, 175, 0.2)',
  },
};

const commonConfig = {
  responsive: true,
  displayModeBar: false,
};

// Render individual charts
const renderAllCharts = () => {
  if (!hasData.value) return;

  const data = sentimentData.value;
  const dates = data.map(d => d.date);

  // Overlay Chart - Price with Vol Regime background
  if (overlayChart.value) {
    const prices = data.map(d => d.price);
    const ivRvRatios = data.map(d => d.iv_rv_ratio);

    // Fixed cycle-based thresholds for IV/RV ratio
    // IV/RV < 1.0 = BUY (cycle bottom, vol cheap)
    // IV/RV > 1.4 = SELL (cycle top, vol expensive)
    const BUY_THRESHOLD = 1.0;
    const SELL_THRESHOLD = 1.4;

    // Create background shapes for vol regime periods
    const shapes = [];
    let currentRegime = null;
    let periodStart = null;

    const validPrices = prices.filter(p => p !== null && p !== undefined);
    const minPrice = Math.min(...validPrices) * 0.95;
    const maxPrice = Math.max(...validPrices) * 1.05;

    for (let i = 0; i < dates.length; i++) {
      const ratio = ivRvRatios[i];
      let regime = null;
      if (ratio !== null && ratio !== undefined) {
        // Mean-reversion thresholds:
        // High IV/RV (>1.4) = fear = BUY opportunity (green)
        // Low IV/RV (<1.0) = complacency = CAUTION (yellow)
        regime = ratio >= SELL_THRESHOLD ? 'buy-zone' : (ratio < BUY_THRESHOLD ? 'caution-zone' : 'neutral');
      }

      if (regime !== currentRegime) {
        if (currentRegime !== null && periodStart !== null) {
          shapes.push({
            type: 'rect',
            xref: 'x',
            yref: 'y',
            x0: periodStart,
            x1: dates[i - 1] || dates[i],
            y0: minPrice,
            y1: maxPrice,
            // Mean-reversion colors: green=BUY opportunity, yellow=CAUTION
            fillcolor: currentRegime === 'buy-zone' ? 'rgba(34, 197, 94, 0.15)' :
                       currentRegime === 'caution-zone' ? 'rgba(234, 179, 8, 0.15)' :
                       'rgba(156, 163, 175, 0.05)',
            line: { width: 0 },
            layer: 'below',
          });
        }
        currentRegime = regime;
        periodStart = dates[i];
      }
    }

    if (currentRegime !== null && periodStart !== null) {
      shapes.push({
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: periodStart,
        x1: dates[dates.length - 1],
        y0: minPrice,
        y1: maxPrice,
        fillcolor: currentRegime === 'buy-zone' ? 'rgba(34, 197, 94, 0.15)' :
                   currentRegime === 'caution-zone' ? 'rgba(234, 179, 8, 0.15)' :
                   'rgba(156, 163, 175, 0.05)',
        line: { width: 0 },
        layer: 'below',
      });
    }

    Plotly.react(overlayChart.value, [
      {
        x: dates,
        y: prices,
        type: 'scatter',
        mode: 'lines',
        name: 'Price',
        line: { color: '#2962FF', width: 2 },
      },
    ], {
      ...commonLayout,
      yaxis: { ...commonLayout.yaxis, title: 'Price ($)', tickformat: '$,.0f', range: [minPrice, maxPrice] },
      shapes: shapes,
      margin: { t: 20, b: 40, l: 70, r: 20 },
    }, commonConfig);
  }

  // IV vs RV Chart
  if (ivRvChart.value) {
    const ivs = data.map(d => d.dvol);
    const rvs = data.map(d => d.realized_vol);

    Plotly.react(ivRvChart.value, [
      {
        x: dates,
        y: ivs,
        type: 'scatter',
        mode: 'lines',
        name: 'IV (DVOL)',
        line: { color: '#FF6D00', width: 2 },
      },
      {
        x: dates,
        y: rvs,
        type: 'scatter',
        mode: 'lines',
        name: 'RV (14d)',
        line: { color: '#2196F3', width: 2 },
      },
    ], {
      ...commonLayout,
      yaxis: { ...commonLayout.yaxis, title: 'Volatility (%)' },
      showlegend: true,
      legend: { x: 0, y: 1.1, orientation: 'h' },
    }, commonConfig);
  }

  // IV/RV Ratio Chart
  if (ivRvRatioChart.value) {
    const ratios = data.map(d => d.iv_rv_ratio);

    Plotly.react(ivRvRatioChart.value, [{
      x: dates,
      y: ratios,
      type: 'scatter',
      mode: 'lines',
      line: { color: '#9C27B0', width: 2 },
      fill: 'tozeroy',
      fillcolor: 'rgba(156, 39, 176, 0.1)',
    }], {
      ...commonLayout,
      yaxis: { ...commonLayout.yaxis, title: 'IV/RV Ratio' },
      shapes: [
        // Mean-reversion: >1.4 = BUY (green), <1.0 = CAUTION (yellow)
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 1.4, y1: 1.4, line: { color: '#22c55e', width: 1, dash: 'dash' } },
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 1.0, y1: 1.0, line: { color: '#eab308', width: 1, dash: 'dash' } },
      ],
      annotations: [
        { x: dates[dates.length - 1], y: 1.4, text: 'BUY zone (fear)', showarrow: false, xanchor: 'right', font: { size: 10, color: '#22c55e' } },
        { x: dates[dates.length - 1], y: 1.0, text: 'CAUTION zone', showarrow: false, xanchor: 'right', font: { size: 10, color: '#eab308' } },
      ],
    }, commonConfig);
  }

  // IV Percentile Chart
  if (ivPercentileChart.value) {
    const percentiles = data.map(d => d.iv_percentile);

    Plotly.react(ivPercentileChart.value, [{
      x: dates,
      y: percentiles,
      type: 'scatter',
      mode: 'lines',
      line: { color: '#00BCD4', width: 2 },
      fill: 'tozeroy',
      fillcolor: 'rgba(0, 188, 212, 0.1)',
    }], {
      ...commonLayout,
      yaxis: { ...commonLayout.yaxis, title: 'IV Percentile (%)', range: [0, 100] },
      shapes: [
        // Mean-reversion: >80% = fear extreme = BUY (green), <20% = complacency = CAUTION (yellow)
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 80, y1: 80, line: { color: '#22c55e', width: 1, dash: 'dash' } },
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 50, y1: 50, line: { color: '#6b7280', width: 1 } },
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 20, y1: 20, line: { color: '#eab308', width: 1, dash: 'dash' } },
      ],
      annotations: [
        { x: dates[dates.length - 1], y: 80, text: 'BUY zone', showarrow: false, xanchor: 'right', font: { size: 10, color: '#22c55e' } },
        { x: dates[dates.length - 1], y: 20, text: 'CAUTION zone', showarrow: false, xanchor: 'right', font: { size: 10, color: '#eab308' } },
      ],
    }, commonConfig);
  }

  // Funding Rate Chart
  if (fundingChart.value) {
    const fundingRates = data.map(d => d.funding_rate);
    const fundingMa7 = data.map(d => d.funding_ma7);
    const colors = fundingRates.map(r => {
      if (r === null || r === undefined) return 'rgba(128, 128, 128, 0.5)';
      return r >= 0 ? 'rgba(239, 68, 68, 0.7)' : 'rgba(34, 197, 94, 0.7)';
    });

    Plotly.react(fundingChart.value, [
      {
        x: dates,
        y: fundingRates,
        type: 'bar',
        marker: { color: colors },
        name: 'Funding',
      },
      {
        x: dates,
        y: fundingMa7,
        type: 'scatter',
        mode: 'lines',
        line: { color: '#3b82f6', width: 2 },
        name: '7d MA',
      }
    ], {
      ...commonLayout,
      yaxis: { ...commonLayout.yaxis, title: 'Funding Rate (%)' },
      shapes: [
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 0, y1: 0, line: { color: '#6b7280', width: 1 } },
      ],
    }, commonConfig);
  }

  // Regime Score Chart
  if (regimeChart.value) {
    const regimeScores = data.map(d => d.regime_score);
    const positiveScores = regimeScores.map(s => (s !== null && s > 0) ? s : null);
    const negativeScores = regimeScores.map(s => (s !== null && s <= 0) ? s : null);

    Plotly.react(regimeChart.value, [
      {
        x: dates,
        y: positiveScores,
        type: 'scatter',
        mode: 'none',
        fill: 'tozeroy',
        fillcolor: 'rgba(34, 197, 94, 0.4)',
        name: 'BUY Zone (Fear)',
      },
      {
        x: dates,
        y: negativeScores,
        type: 'scatter',
        mode: 'none',
        fill: 'tozeroy',
        fillcolor: 'rgba(234, 179, 8, 0.4)',
        name: 'CAUTION Zone',
      },
      {
        x: dates,
        y: regimeScores,
        type: 'scatter',
        mode: 'lines',
        line: { color: '#374151', width: 2 },
        name: 'Regime Score',
      }
    ], {
      ...commonLayout,
      yaxis: { ...commonLayout.yaxis, title: 'Signal Score' },
      shapes: [
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 0.4, y1: 0.4, line: { color: '#22c55e', width: 1, dash: 'dash' } },
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: -0.4, y1: -0.4, line: { color: '#eab308', width: 1, dash: 'dash' } },
        { type: 'line', x0: dates[0], x1: dates[dates.length - 1], y0: 0, y1: 0, line: { color: '#6b7280', width: 1 } },
      ],
      annotations: [
        { x: dates[dates.length - 1], y: 0.4, text: 'BUY Zone', showarrow: false, xanchor: 'right', font: { size: 10, color: '#22c55e' } },
        { x: dates[dates.length - 1], y: -0.4, text: 'CAUTION Zone', showarrow: false, xanchor: 'right', font: { size: 10, color: '#eab308' } },
      ],
    }, commonConfig);
  }
};

// Event handlers
const handleClickOutside = (event) => {
  if (!event.target.closest('.dropdown') && !event.target.closest('.relative')) {
    showDropdown.value = false;
  }
};

const handleResize = () => {
  if (hasData.value) {
    [overlayChart, ivRvChart, ivRvRatioChart, ivPercentileChart, fundingChart, regimeChart].forEach(chart => {
      if (chart.value) {
        Plotly.Plots.resize(chart.value);
      }
    });
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  [overlayChart, ivRvChart, ivRvRatioChart, ivPercentileChart, fundingChart, regimeChart].forEach(chart => {
    if (chart.value) {
      Plotly.purge(chart.value);
    }
  });
});

// Watchers
watch(days, () => {
  if (hasData.value) {
    fetchSentimentData();
  }
});

watch(timeframe, (newTf) => {
  const validDays = dayOptionsByTimeframe[newTf] || [30, 60, 90];
  if (!validDays.includes(days.value)) {
    days.value = validDays[0];
  }
  if (hasData.value) {
    fetchSentimentData();
  }
});
</script>

<style scoped>
.sentiment-explorer-tab {
  width: 100%;
}

.chart-panel-large {
  width: 100%;
  height: 350px;
}

.chart-panel {
  width: 100%;
  height: 200px;
}

@media (max-width: 639px) {
  .chart-container {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel-large {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel,
  .analytics-chart {
    height: 280px;
    min-height: 240px;
  }
}
</style>
